import type { EnhancedStore } from '@reduxjs/toolkit';
import { Project } from './Project';
import { selectDraftById, selectDrafts } from "@/store/slices/drafts/selectors";
import { saveDraft, updateDraft, clearDrafts, duplicateDraft, removeDraft } from "@/store/slices/drafts/actions";
import { TemplateConfig } from "@/interfaces/editor";
import { nanoid } from "nanoid";
import IndexedDBSingleton from "@/utils/IndexedDB";
import storage from "@/store/swIndexedDBStorage";

export class BtDraftsManager {
    private static store: EnhancedStore | undefined;

    static setStore(store: EnhancedStore) {
        BtDraftsManager.store = store;
    }

    private static getStore(): EnhancedStore {
        if (!BtDraftsManager.store) {
            throw new Error("BtDraftsManager store is not set");
        }
        return BtDraftsManager.store;
    }
    static getDrafts(): Project[] {
        const drafts = selectDrafts(this.getStore().getState());
        return drafts.map(Project.fromState);
    }

    static get(draftId: string): Project | undefined {
        let draft = selectDraftById(draftId)(this.getStore().getState());
        return Project.fromState(draft)
    }

    static saveDraft(draft: Project): Project {
        const action = saveDraft(draft);
        this.getStore().dispatch(action);
        return this.get(draft.projectId) || draft;
    }

    static updateDrafts(draft: Project): Project {
        const action = updateDraft(draft);
        this.getStore().dispatch(action);
        return this.get(draft.projectId) || draft;
    }

    static removeDraft(draftId: string) {
        const action = removeDraft(draftId);
        this.getStore().dispatch(action);
    }

    static updateDraftsWithDraft(draft: Project, otherDraft?: Project): Project {
        if (!otherDraft) {
            return this.updateDrafts(draft);
        }
        const action = updateDraft(draft);
        this.getStore().dispatch(action);
        
        return this.get(draft.projectId) || draft;
    }

    static duplicateDraft(draft: Project): Project {
        let id = nanoid();
        let newDraft = new Project(id);
        newDraft.templateConfig = { ...draft.templateConfig, draftGuid: id };

        const action = duplicateDraft(newDraft);
        this.getStore().dispatch(action);

        return this.get(id) || newDraft;
    }

    static createDraftFromTemplateConfig(templateConfig: TemplateConfig): Project {
        let project = new Project(templateConfig.draftGuid);
        project.templateConfig = {...templateConfig};
        return project;
    }

    static async clear() {
        const action = clearDrafts();
        this.getStore().dispatch(action);

        await IndexedDBSingleton.deleteByPrefix('/projects');

        // Clear redux-persist storage for drafts to prevent rehydration after logout
        await storage.removeItem('persist:drafts');
    }

    static createDraft(projectId: string): Project {
        return Project.createDraft(projectId)
    }
}
